import { isEmpty } from 'lodash';
import { EDITOR } from 'constants/actionTypes';
import getSections from 'reducers/editor/sections';
import reduceElements from 'reducers/editor/elements';
import reduceIntegrations from 'reducers/editor/integrations';
import reduceCommentary, {
  initialState as commentary,
} from 'reducers/editor/commentary';

const initialState = {
  lessonId: 0,
  lessonTitle: 0,
  sections: [],
  currentSection: {},
  commentary,
  isSaving: false,
  isFetching: false,
  fullwidth: false,
  url: null,
  success: true,
  imageLibraryDialog: { visible: false, image: null },
  isDraggingElement: false,
  lastUpdatedAt: null,
  publishedAt: null,
};

const editorReducer = (state = initialState, action) => {
  switch (action.type) {
    /* Lesson actions */
    case EDITOR.LESSON.UPDATE:
      return {
        ...state,
        lessonTitle: action.title,
      };

    case EDITOR.TEMPLATE.UPDATE:
      return {
        ...state,
        lessonTitle: action.title,
        lessonDescription: action.description,
      };

    case EDITOR.TEMPLATE.TOGGLE_STATUS:
      return {
        ...state,
        templateActive: action.status,
      };

    case EDITOR.TEMPLATE.IMAGE_CHANGE: {
      const { imageType } = action;
      return {
        ...state,
        templates: {
          ...state.templates,
          [imageType]: {
            ...state.templates[imageType],
            image: action.image,
          },
        },
      };
    }

    case EDITOR.TEMPLATE.TOGGLE_ADD_TO_LIBRARY: {
      const { visible, imageType } = action;
      return {
        ...state,
        templates: {
          ...state.templates,
          [imageType]: {
            ...state.templates[imageType],
            addToLibraryVisible: visible,
          },
        },
      };
    }

    case EDITOR.TEMPLATE.TOGGLE_IMAGE_LIBRARY: {
      const { visible, imageType } = action;
      return {
        ...state,
        templates: {
          ...state.templates,
          [imageType]: {
            ...state.templates[imageType],
            imageLibraryVisible: visible,
          },
        },
      };
    }

    case EDITOR.TEMPLATE.TOGGLE_IMAGE_SUCCESS:
      return {
        ...state,
        templateImageSuccess: action.success,
      };

    case EDITOR.LESSON.REFRESH_INVALID_SECTIONS:
      return {
        ...state,
        sectionsWithInvalidElements: action.sectionsWithInvalidElements,
      };

    /* Section actions */
    case EDITOR.SECTION.CREATE:
    case EDITOR.SECTION.UPDATE:
    case EDITOR.SECTION.REORDER:
    case EDITOR.SECTION.DUPLICATE:
    case EDITOR.SECTION.DELETE: {
      const { sections: currentSections, currentSection } = state;
      const sections = getSections(currentSections, action);
      return {
        ...state,
        sections,
        isFetching: false,
        currentSection: !isEmpty(sections) ? currentSection : {},
      };
    }

    case EDITOR.SECTION.REQUEST_EDIT:
    case EDITOR.SECTION.REQUEST_CREATE: {
      return { ...state, isFetching: true };
    }

    case EDITOR.SECTION.EDIT:
      return {
        ...state,
        currentSection: action.payload,
        isFetching: false,
      };

    case EDITOR.SECTION.UPDATE_URL:
      return {
        ...state,
        url: action.url,
      };

    case EDITOR.COMMENTARY.SHOW:
    case EDITOR.COMMENTARY.HIDE:
    case EDITOR.COMMENTARY.REQUEST:
    case EDITOR.COMMENTARY.REQUEST_SUCCESS:
    case EDITOR.COMMENTARY.REQUEST_FAILURE:
    case EDITOR.COMMENTARY.RESOLVE_THREAD:
    case EDITOR.COMMENTARY.POST_REPLY:
    case EDITOR.COMMENTARY.CREATE_THREAD:
    case EDITOR.COMMENTARY.SET_ACTIVE_THREAD:
    case EDITOR.COMMENTARY.SET_UNACTIVE_THREAD:
      return {
        ...state,
        commentary: reduceCommentary(state.commentary, action),
      };

    /* Element Actions */
    case EDITOR.ELEMENT.CREATE:
    case EDITOR.ELEMENT.DELETE:
    case EDITOR.ELEMENT.DELETE_CHILDREN:
    case EDITOR.ELEMENT.APPLY_CHANGES:
    case EDITOR.ELEMENT.SET_VALIDITY:
    case EDITOR.ELEMENT.REORDER:
    case EDITOR.ELEMENT.REPLACE:
    case EDITOR.ELEMENT.DETACH_PARENT:
    case EDITOR.ELEMENT.ASSIGN_PARENT:
    case EDITOR.ELEMENT.UPDATE_COMMENTS_COUNT:
      return {
        ...state,
        currentSection: {
          ...state.currentSection,
          elements: reduceElements(state.currentSection.elements, action),
        },
      };

    case EDITOR.INTEGRATIONS.DATA_REQUEST:
    case EDITOR.INTEGRATIONS.DATA_RECEIVE:
    case EDITOR.INTEGRATIONS.DATA_FAILURE:
      return {
        ...state,
        ...state.integrations,
        integrations: reduceIntegrations(state.integrations, action),
      };

    /* Autosave actions */
    case EDITOR.AUTOSAVE.REQUEST:
      return { ...state, isSaving: true };

    case EDITOR.AUTOSAVE.SUCCESS:
      return { ...state, isSaving: false, lastUpdatedAt: new Date() };

    case EDITOR.AUTOSAVE.FAILURE:
      return { ...state, isSaving: false, success: false };

    case EDITOR.TOGGLE.DRAGGING_ELEMENT:
      return { ...state, isDraggingElement: !state.isDraggingElement };

    case EDITOR.TOGGLE.ADD_TO_IMAGE_LIBRARY:
      return {
        ...state,
        imageLibraryDialog: {
          ...state.imageLibraryDialog,
          visible: !state.imageLibraryDialog.visible,
          image: action.image,
        },
      };

    default:
      return {
        ...initialState,
        ...state,
        // @FIXME (backend) currentSection should not be null
        currentSection: state.currentSection || {},
      };
  }
};

export default editorReducer;
